import { Action, ActionCreator, Reducer } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { makeCopy } from '../util/helpers';
import { mapConsentFeaturesTo, getAllFeatures } from '../util/consents';
import ClientComs from '../classes/ClientComs';
import Client4p from '../classes/Client4p';
import {
  ConsentGroup, ConsentSaveType, ConsentType, FeatureItemType, PageDetails,
} from '../types/common';
import type { RootState } from './index';
import { FeatureType } from '../defs/features';

// action types

export const LOAD = 'consents/LOAD';
export const LOAD_VIEW = 'consents/LOAD_VIEW';
export const SAVE = 'consents/SAVE';
export const TOGGLE = 'consents/TOGGLE';
export const TOGGLE_MERGE_ACCEPTED = 'consents/MERGECHANGE';
export const ONECLICK = 'consents/ONECLICK';
export const SET_COLLAPSIBLE_OPEN = 'consents/SET_COLLAPSIBLE_OPEN';
export const SET_EDIT_ACTIVE_GROUP_IND = 'consents/SET_EDIT_ACTIVE_GROUP_IND';
export const CANCEL_EDIT_MODE = 'consents/CANCEL_EDIT_MODE';
export const CANCEL_PAGE = 'consents/CANCEL_PAGE';
export const CANCEL_MICROFRONTEND = 'action/CANCEL_MICROFRONTEND';
export const DENY = 'consents/DENY';
export const PROCESSING = 'consents/PROCESSING';
export const ERROR = 'consents/ERROR';

// action creators

export const setCollpsibleOpen: ActionCreator<Action> = (
  groupName: string,
) => ({
  type: SET_COLLAPSIBLE_OPEN,
  groupName,
});

export const setActiveGroupInd = (groupInd = -1) => ({
  type: SET_EDIT_ACTIVE_GROUP_IND,
  groupInd,
});

export const cancelEditMode = () => ({
  type: CANCEL_EDIT_MODE,
});

export const cancelPage = () => ({
  type: CANCEL_PAGE,
});

export const cancelMicrofrontend = () => ({
  type: CANCEL_MICROFRONTEND,
});

export const load: ActionCreator<ThunkAction<Promise<Action>, RootState, void, Action>> = () => async (
  dispatch,
  getState,
): Promise<Action> => {
  try {
    dispatch({ type: PROCESSING });
    const { config, auth, consents } = getState();
    const { mode, lifecycle, apiUri4p } = config;
    const { spId, registerId } = auth;

    const { globalOfferId } = consents;
    // check if it's a known registerId and reload consents
    if (globalOfferId && globalOfferId === `${spId}${registerId}`) {
      dispatch({
        type: LOAD,
        consentGroups: consents.consentGroups,
        globalOfferId,
      });
      return;
    }

    const client = apiUri4p
      ? new Client4p(config, auth)
      : new ClientComs(config, auth);

    let offerId;

    // mode might now contain a comma seperated list of modes which we need to ignore here
    // and just take care of the first in the list
    const [modePart1, modePart2] = mode.replace(/,.*$/, '').split(':');

    if (modePart1 === 'multiedit') {
      const lifecycleView = `${lifecycle}View`;
      const { groups } = await client.getConsents(lifecycleView);

      dispatch({
        type: LOAD_VIEW,
        consentGroups: groups,
      });
    }

    const { groups, details } = await client.getConsents(lifecycle);

    if (registerId) {
      offerId = `${spId}${registerId}`;
    }

    dispatch({
      type: LOAD,
      consentGroups: groups,
      globalOfferId: offerId,
      details,
    });

    // if mode = multiedit:xxx then try to open edit mode for group
    if (modePart1 === 'multiedit' && typeof modePart2 !== 'undefined') {
      // eslint-disable-next-line no-restricted-globals
      if (isNaN((modePart2 as unknown) as number)) {
        // modePart2 is group name
        dispatch(
          setActiveGroupInd(groups.findIndex(({ name }) => name === modePart2)),
        );
      } else if (groups[+modePart2]) {
        // modePart2 is numeric
        dispatch(setActiveGroupInd(+modePart2));
      }
    }
  } catch (error) {
    dispatch({
      type: ERROR,
      error,
    }); // TypeError if network error, timeout, or CORS problems
    throw error;
  }
};

export const save: ActionCreator<ThunkAction<void, RootState, void, Action>> = () => async (
  dispatch,
  getState,
): Promise<void> => {
  try {
    dispatch({ type: PROCESSING });
    const { config, auth, consents } = getState();
    const { apiUri4p } = config;
    const { consentGroups = [] } = consents;
    const client = apiUri4p
      ? new Client4p(config, auth)
      : new ClientComs(config, auth);

    // save consents
    if (consentGroups.length) {
      const data: ConsentSaveType[] = []; // collect output data...
      consentGroups.forEach(({ consents: conss = [], required }) => {
        conss
          .filter(({ deprecated }) => !deprecated)
          .forEach(({ id, granted = false, ...features }) => {
            const featureData = mapConsentFeaturesTo(
              features as ConsentType,
              ({ id: fid, granted: fgranted = false }) => ({
                id: fid,
                granted: required ? true : fgranted,
              }),
            );
            data.push({
              id,
              granted: required ? true : granted,
              ...featureData,
            });
          });
      });

      await client.saveConsents(data);
    }
    dispatch({ type: SAVE });
  } catch (error) {
    dispatch({ type: ERROR, error }); // TypeError if network error, timeout, or CORS problems
    throw error;
  }
};

export const saveAndLoad: ActionCreator<ThunkAction<void, RootState, void, Action>> = () => async (
  dispatch,
): Promise<void> => {
  // eslint-disable-next-line @typescript-eslint/await-thenable
  await dispatch(save());
  await dispatch(load());
  dispatch(setActiveGroupInd());
};

export const toggle: ActionCreator<ThunkAction<void, RootState, void, Action>> = (
  { id, fid, feature }: { id: string, fid?: string, feature?: string },
) => async (
  dispatch,
  getState,
) => { // eslint-disable-line @typescript-eslint/require-await
  const {
    consents: { consentGroups },
  } = getState();
  // find affected consent and features
  let consentIndex: number;
  const groupIndex = consentGroups.findIndex(({ consents }) => {
    consentIndex = consents.findIndex(({ id: cid }) => cid === id);
    return consentIndex > -1;
  });
  const newConsent = makeCopy<ConsentType>(
    consentGroups[groupIndex].consents[consentIndex],
  );
  const fValues = feature ? newConsent[feature as FeatureType] : undefined;
  const fValue = fValues
    ? fValues.find(({ id: fid2 }) => fid2 === fid)
    : undefined;
  const fValuesAll = getAllFeatures(newConsent);
  // set values consistenty
  if (fValue) {
    // click on feature checkbox
    fValue.granted = !fValue.granted;
    if (fValue.granted) {
      newConsent.granted = true;
    } else {
      const anyChecked = fValuesAll.find(({ granted }) => granted);
      if (!anyChecked) {
        newConsent.granted = false;
      }
    }
  } else {
    // click on consent checkbox
    newConsent.granted = !newConsent.granted;
    fValuesAll.forEach((fV) => {
      // eslint-disable-next-line no-param-reassign
      fV.granted = newConsent.granted;
    });
  }
  // update affected part of state to re-render view
  const newGroupConsents = [...consentGroups[groupIndex].consents];
  newGroupConsents.splice(consentIndex, 1, newConsent);
  const newGroup = {
    ...consentGroups[groupIndex],
    consents: newGroupConsents,
  };
  const newConsentGroups = [...consentGroups];
  newConsentGroups.splice(groupIndex, 1, newGroup);

  dispatch({
    type: TOGGLE,
    consentGroups: newConsentGroups,
  });
};

const changeAllGroupConsents = (group2Change: ConsentGroup, value: boolean) => group2Change.consents.map((consent) => {
  const featureData = mapConsentFeaturesTo<FeatureItemType>(consent, (f) => ({
    ...f,
    granted: value,
  } as FeatureItemType));
  return {
    ...consent,
    granted: value,
    ...featureData,
  };
});

export const oneclick: ActionCreator<ThunkAction<void, RootState, void, Action>> = (
  groupIndex: number,
  checked: boolean,
) => async (
  dispatch,
  getState,
// eslint-disable-next-line @typescript-eslint/require-await
) => {
  const {
    consents: { consentGroups },
  } = getState();
  const oneclickGroup = consentGroups[groupIndex];
  const newGroupConsents = changeAllGroupConsents(oneclickGroup, checked);

  // update affected part of state to re-render view
  const newGroup = {
    ...consentGroups[groupIndex],
    consents: newGroupConsents,
  };
  const newConsentGroups = [...consentGroups];
  newConsentGroups.splice(groupIndex, 1, newGroup);

  dispatch({
    type: ONECLICK,
    consentGroups: newConsentGroups,
  });
};

export const deny: ActionCreator<ThunkAction<void, RootState, void, Action>> = (
  groupIndex: number,
  checked: boolean,
) => async (
  dispatch,
  getState,
) => { // eslint-disable-line @typescript-eslint/require-await
  const {
    consents: { consentGroups, consentGroupsOrigin },
  } = getState();
  // set all consents to not granted
  const denyGroup = consentGroups[groupIndex];
  let newGroupConsents = denyGroup.consents;
  if (checked) {
    // set all consents of the group to false
    newGroupConsents = changeAllGroupConsents(denyGroup, false);
  } else {
    // set the consents to the original consents again
    newGroupConsents = consentGroupsOrigin[groupIndex].consents;
  }
  // update affected part of state to re-render view
  const newGroup = {
    ...consentGroups[groupIndex],
    consents: newGroupConsents,
  };
  const newConsentGroups = [...consentGroups];
  newConsentGroups.splice(groupIndex, 1, newGroup);

  dispatch({
    type: DENY,
    consentGroups: newConsentGroups,
  });
};

// initial state

export type ConsentsState = {
  details?: PageDetails,
  mergeAccepted: boolean,
  consentGroups?: ConsentGroup[];
  consentGroupsView?: ConsentGroup[];
  consentGroupsOrigin?: ConsentGroup[];
  editmodeActiveGroupInd: number;
  processing: boolean;
  error: boolean;
  globalOfferId?: string;
  collapsibleOpenGroupName?: string;
};

const initial: ConsentsState = {
  consentGroups: undefined,
  details: undefined,
  mergeAccepted: false,
  consentGroupsView: undefined,
  consentGroupsOrigin: undefined,
  editmodeActiveGroupInd: -1,
  processing: false,
  error: false,
  globalOfferId: undefined,
  collapsibleOpenGroupName: undefined,
};

type ConsentsAction = Action<
| typeof CANCEL_EDIT_MODE
| typeof CANCEL_PAGE
| typeof SET_EDIT_ACTIVE_GROUP_IND
| typeof SET_COLLAPSIBLE_OPEN
| typeof LOAD_VIEW
| typeof LOAD
| typeof SAVE
| typeof TOGGLE
| typeof TOGGLE_MERGE_ACCEPTED
| typeof ONECLICK
| typeof DENY
| typeof PROCESSING
| typeof ERROR
> &
ConsentsState & {
  groupInd?: number;
  groupName?: string;
};

// reducer

export const reducer: Reducer<ConsentsState, ConsentsAction> = (
  state = initial,
  action = {} as ConsentsAction,
) => {
  const {
    type,
    consentGroups,
    error,
    globalOfferId,
    groupInd,
    groupName,
    details,
  } = action;
  const { editmodeActiveGroupInd, mergeAccepted } = state;
  switch (type) {
    case CANCEL_EDIT_MODE:
      // have to reset consent status from consentGroupsView
      // eslint-disable-next-line no-case-declarations
      const consentGroupsToBeSet = [...state.consentGroups];
      // eslint-disable-next-line no-case-declarations
      const consentsPersistCopy = makeCopy(
        state.consentGroupsOrigin[editmodeActiveGroupInd].consents,
      );

      consentGroupsToBeSet[
        editmodeActiveGroupInd
      ].consents = consentsPersistCopy;
      return {
        ...state,
        editmodeActiveGroupInd: -1,
        consentGroups: consentGroupsToBeSet,
      };
    case CANCEL_PAGE:
      return {
        ...state,
        consentGroups: makeCopy(state.consentGroupsOrigin),
      };
    case SET_EDIT_ACTIVE_GROUP_IND:
      return { ...state, editmodeActiveGroupInd: groupInd };
    case SET_COLLAPSIBLE_OPEN:
      return { ...state, collapsibleOpenGroupName: groupName };
    case LOAD_VIEW:
      return { ...state, consentGroupsView: consentGroups };
    case LOAD:
      return {
        ...state,
        consentGroups,
        consentGroupsOrigin: makeCopy(consentGroups),
        processing: false,
        error: false,
        globalOfferId,
        details,
      };
    case SAVE:
      return { ...state, processing: false, error: false };
    case TOGGLE_MERGE_ACCEPTED:
      return { ...state, mergeAccepted: !mergeAccepted };
    case ONECLICK:
    case DENY:
    case TOGGLE:
      return { ...state, consentGroups };
    case PROCESSING:
      return { ...state, processing: true, error: false };
    case ERROR:
      return { ...state, processing: false, error };
    default:
      return state;
  }
};
